// @ts-check
"use strict"

import {
  anyChar, anyCharExcept, char, choice, digits, endOfInput, lookAhead, many, optionalWhitespace, Parser, possibly, recursiveParser, sequenceOf, str
} from "arcsecond"

/**
 * @param {string} json
 * @returns {Record<string, *>}
 */
export function parseReportJson(json) {
  const result = $lenientReportValue.run(json)
  if(result.isError) throw result.error
  // @ts-ignore
  return result.result
}

/*
  * ******************************
  * ******************************
  *
  * LENIENT JSON PARSER
  *
  * ******************************
  * ******************************
  */
const $lenientReportValue = recursiveParser(() => (
  between(
    possibly(many(anyCharExcept(char("{")))),
    possibly(many(anyChar))
  )(
    $objectValue
  ).map(([_1, jsonValue, _2]) => jsonValue)
))

/** @type {Parser<null | boolean | string | number | unknown[] | Record<string, unknown>>} */
const $lenientJsonValue = recursiveParser(() => (
  choice([
    $nullValue,
    $booleanValue,
    $objectValue,
    $arrayValue,
    $numberValue,
    $stringValue,
  ])  
))

/** @type {Parser<null>} */
const $nullValue = streamedStr("null").map(() => null)

const $booleanValue = choice([
  streamedStr("true"),
  streamedStr("false")
]).map(value => value === "true")

// Parses integers and decimals (e.g. 42, -3.14). No scientific notation needed for chart data.
const $numberValue = sequenceOf([
  possibly(char("-")),
  digits,
  possibly(sequenceOf([char("."), digits])),
]).map(([sign, intPart, fracPart]) => {
  const fracStr = fracPart ? "." + fracPart[1] : ""
  return Number((sign ?? "") + intPart + fracStr)
})

const $escapeSequence = choice([
  str("\\n").map(_ => "\n"),
  str("\\r").map(_ => "\r"),
  str("\\t").map(_ => "\t"),
  str("\\\\").map(_ => "\\"),
  str("\\\"").map(_ => "\""),
])

/** @type {Parser<string>} */
const $doubleQuotedString = between(char("\""), char("\""))(
  many(
    choice([
      $escapeSequence,
      anyCharExcept(
        choice([
          char("\""),
          char("\n"),
          char("\r"),
          char("\t"),
        ])
      )
    ])
  ).map(chars => chars.join(""))
).map(([_1, value, _2]) => value ?? "")

/** @type {Parser<string>} */
const $singleQuotedString = between(char("'"), char("'"))(
  many(
    choice([
      $escapeSequence,
      anyCharExcept(
        choice([
          char("'"),
          char("\n"),
          char("\r"),
          char("\t"),
        ])
      )
    ])
  ).map(chars => chars.join(""))
).map(([_1, value, _2]) => value ?? "")

const $unquotedString = many(
  choice([
    $escapeSequence,
    anyCharExcept(
      choice([
        char("\""),
        char("'"),
        char(":"),
        char(","),
        char("}"),
        char("]"),
        char("\n"),
        char("\r"),
        char("\t"),
      ])
    )
  ])
).map(chars => chars.join(""))

const $stringValue = choice([
  $doubleQuotedString,
  $singleQuotedString,
  $unquotedString
])

const $arrayValue = between(char("["), char("]"))(
  sequenceOf([
    optionalWhitespace,
    possibly(
      separatedBy(char(","))(
        $lenientJsonValue
      )
    ),
    optionalWhitespace
  ]).map(([_1, values, _2]) => values)
).map(([_1, values, _2]) => values ?? [])

const $name = choice([
  $doubleQuotedString,
  $singleQuotedString,
  $unquotedString
])

const $keyValuePair = between($name, $lenientJsonValue)(
  sequenceOf([
    optionalWhitespace,
    char(":"),
    optionalWhitespace
  ])
).map(([name, _, value]) => /**@type{const}*/([name, value]))

const $objectValue = between(char("{"), char("}"))(
  sequenceOf([
    optionalWhitespace,
    possibly(
      separatedBy(char(","))($keyValuePair)
    ),
    optionalWhitespace
  ]).map(([_1, values, _2]) => values)
).map(([_1, values, _2]) => (
  Object.fromEntries(
    // @ts-ignore
    (values ?? []).filter(([key, _]) => key !== "")
  )
))

/**
 * @param {string} text
 * @returns {Parser<string>}
 */
function streamedStr(text) {
  return (
    choice(
      text.split("").map((_, i) => (
        str(text.slice(0, i + 1))
      ))
    )
  )
}

/**
 * @template S, E, V
 * @param {Parser<S>} startParser 
 * @param {Parser<E>} endParser
 */
function between(startParser, endParser) {
  /** @param {Parser<V>} valueParser */
  return (valueParser) => (
    sequenceOf([
      startParser,
      choice([
        endOfInput.map(_ => /**@type{const}*/([null, null])),
        sequenceOf([
          valueParser,
          choice([
            endOfInput.map(_ => null),
            endParser,
          ]),
        ])
      ])
    ]).map(([start, [value, end]]) => /**@type{const}*/([start, value, end]))
  )
}

/**
 * @template S, V
 * @param {Parser<S>} separatorParser 
 */
function separatedBy(separatorParser) {
  /** @param {Parser<V>} valueParser */
  return (valueParser) => (
    sequenceOf([
      valueParser,
      possibly(
        many(
          choice([
            sequenceOf([
              optionalWhitespace,
              endOfInput,
            ]).map(_ => EMPTY_VALUE),
            sequenceOf([
              optionalWhitespace,
              separatorParser,
              optionalWhitespace,
              endOfInput
            ]).map(_ => EMPTY_VALUE),
            sequenceOf([
              optionalWhitespace,
              separatorParser,
              optionalWhitespace,
              valueParser
            ]).map(([_1, _2, _3, value]) => value)
          ])
        ).map(values => values.filter(value => value !== EMPTY_VALUE)),
      )
    ]).map(([firstValue, restOfValues]) => [firstValue, ...(restOfValues ?? [])])
  )
}

const EMPTY_VALUE = Symbol("empty")

/**
 * @template T
 * @typedef {Object} Result
 * @property {T} value
 * @property {Error | null} error
 */